import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Search,
  CheckCircle2,
  Clock,
  MessageSquare,
  ShieldAlert,
  X,
  FileText
} from 'lucide-react';
import { AdminDisputeItem } from '../../../types/admin.types';
import {
  fetchAdminDisputes,
  resolveAdminDisputeApi,
} from '../../../lib/adminService';

interface DisputesTabProps {
  onShowNotice: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export const DisputesTab: React.FC<DisputesTabProps> = ({ onShowNotice }) => {
  const [disputes, setDisputes] = useState<AdminDisputeItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState<AdminDisputeItem | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadDisputes = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminDisputes();
      setDisputes(data);
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to load disputes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDisputes();
  }, []);

  const handleResolve = async (dispute: AdminDisputeItem) => {
    if (!resolutionNote.trim()) {
      onShowNotice('Resolution note is required to close a dispute', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await resolveAdminDisputeApi(dispute.id, 'RESOLVED_BY_ADMIN', resolutionNote);
      onShowNotice(`Dispute ${dispute.id} resolved successfully.`, 'success');
      setSelectedDispute(null);
      setResolutionNote('');
      loadDisputes();
    } catch (err: any) {
      onShowNotice(err.message || 'Failed to resolve dispute', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-black text-white tracking-tight">Dispute Resolution Desk</h2>
            <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono text-xs font-bold">
              {disputes.filter((d) => d.status === 'OPEN' || d.status === 'UNDER_REVIEW').length} Active
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Section 24: Conflict mediation between clients and companions with evidence records and case resolutions.
          </p>
        </div>

        <button
          type="button"
          onClick={loadDisputes}
          className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer self-start sm:self-auto"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-purple-400' : ''}`} />
        </button>
      </div>

      {/* Disputes Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950/60 text-slate-400 font-mono uppercase text-[10px] border-b border-slate-800">
              <tr>
                <th className="py-3 px-4">Dispute ID</th>
                <th className="py-3 px-4">Booking Ref</th>
                <th className="py-3 px-4">Client</th>
                <th className="py-3 px-4">Companion</th>
                <th className="py-3 px-4">Reason / Issue</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {disputes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    No disputes recorded.
                  </td>
                </tr>
              ) : (
                disputes.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 px-4 font-mono font-bold text-amber-400">{d.id}</td>
                    <td className="py-3.5 px-4 font-mono text-purple-400">{d.bookingId}</td>
                    <td className="py-3.5 px-4 font-bold text-white">{d.clientName}</td>
                    <td className="py-3.5 px-4 text-slate-200">{d.companionName}</td>
                    <td className="py-3.5 px-4 text-slate-300 max-w-sm truncate">{d.reason}</td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono border ${
                          d.status === 'RESOLVED'
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        }`}
                      >
                        {d.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedDispute(d)}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition cursor-pointer"
                      >
                        Review Case
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Review & Resolve Modal */}
      {selectedDispute && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-lg w-full space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span>Dispute Case #{selectedDispute.id}</span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDispute(null)}
                className="text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-400"><strong>Booking Ref:</strong> {selectedDispute.bookingId}</div>
                <div className="text-slate-400"><strong>Parties:</strong> {selectedDispute.clientName} vs {selectedDispute.companionName}</div>
                <div className="text-slate-200 mt-2"><strong>Reported Issue:</strong> {selectedDispute.reason}</div>
              </div>

              {selectedDispute.internalNotes.length > 0 && (
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1 text-slate-400">
                  <span className="font-bold text-slate-300 block mb-1">Internal Notes:</span>
                  {selectedDispute.internalNotes.map((note, idx) => (
                    <div key={idx}>• {note}</div>
                  ))}
                </div>
              )}

              {selectedDispute.status !== 'RESOLVED' && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-400 mb-1">
                    Administrative Resolution Note: *
                  </label>
                  <textarea
                    rows={3}
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                    placeholder="Document resolution outcome, compensation agreement, or close justification..."
                    className="w-full p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setSelectedDispute(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold cursor-pointer"
              >
                Close
              </button>
              {selectedDispute.status !== 'RESOLVED' && (
                <button
                  type="button"
                  disabled={isSubmitting || !resolutionNote.trim()}
                  onClick={() => handleResolve(selectedDispute)}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Resolving...' : 'Resolve Dispute'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
